/**
 * ColorCard.tsx
 * Reusable component for displaying HeroUI color demonstrations
 * Shows buttons, chips, badges, and links for a specific color
 */

import React from 'react';
import { Card, CardHeader, CardBody } from '@enfomentis/ui';
import { Button } from '@enfomentis/ui';
import { Badge } from '@enfomentis/ui';
import { Chip } from '@enfomentis/ui';
import { Link } from '@enfomentis/ui';

interface ColorCardProps {
  color: 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'danger';
}

export const ColorCard: React.FC<ColorCardProps> = ({ color }) => {
  return (
    <Card>
      <CardHeader>
        <h5 className="font-medium text-text-primary capitalize">{color}</h5>
      </CardHeader>
      <CardBody>
        <div className="space-y-4">
          {/* Buttons */}
          <div className="text-center">
            <div className="text-xs font-medium text-default-500 mb-2">Buttons:</div>
            <div className="flex flex-wrap justify-center gap-2">
              <Button color={color} size="sm">Solid</Button>
              <Button color={color} variant="bordered" size="sm">Bordered</Button>
              <Button color={color} variant="flat" size="sm">Flat</Button>
              <Button color={color} variant="ghost" size="sm">Ghost</Button>
            </div>
          </div>
          
          {/* Chips */}
          <div className="text-center">
            <div className="text-xs font-medium text-default-500 mb-2">Chips:</div>
            <div className="flex flex-wrap justify-center gap-2">
              <Chip color={color} size="sm">Default</Chip>
              <Chip color={color} variant="bordered" size="sm">Bordered</Chip>
              <Chip color={color} variant="dot" size="sm">Dot</Chip>
              <Chip color={color} variant="flat" size="sm">Flat</Chip>
            </div>
          </div>
          
          {/* Badges */}
          <div className="text-center">
            <div className="text-xs font-medium text-default-500 mb-2">Badges:</div>
            <div className="flex justify-center gap-3">
              <Badge color={color} content="3">
                <div className="px-3 py-1 bg-content2 rounded-lg text-sm">Badge</div>
              </Badge>
              <Badge color={color} variant="flat" content="99+">
                <div className="px-3 py-1 bg-content2 rounded-lg text-sm">Flat Badge</div>
              </Badge>
            </div>
          </div>
          
          {/* Links */}
          <div className="text-center">
            <div className="text-xs font-medium text-default-500 mb-2">Links:</div>
            <div>
              <Link color={color === 'default' ? 'foreground' : color} size="sm">Link Color</Link>
            </div>
          </div>
        </div>
      </CardBody>
    </Card>
  );
};